/**
 * CoverageDot
 * -----------
 * Tiny green/grey dot indicating whether an athlete is currently covered
 * by an active subscription, session pack, family-wide pool, or comp
 * access — surfaces the entitlement state coaches care about most.
 *
 * Backend source: `coverage` field on each athlete in the lead-detail
 * payload (see routes/coaches.py::_build_lead_detail::_athlete_coverage).
 *
 * Colors mirror the booking gate's contract:
 *   • green  — covered (sub OR pack OR family pool OR comp access)
 *   • grey   — not covered (would fail _has_schedule_entitlement)
 *   • amber  — covered-but-flagged (dunning suspension)
 *
 * Hover surfaces the human-readable source (e.g. "Family pool: 10-pack
 * · 7/10 left") so the coach knows where the coverage is coming from.
 */

const SOURCE_COLORS = {
    subscription:        { dot: "bg-emerald-400", ring: "ring-emerald-500/30" },
    family_subscription: { dot: "bg-emerald-400", ring: "ring-emerald-500/30" },
    session_pack:        { dot: "bg-emerald-400", ring: "ring-emerald-500/30" },
    family_pack:         { dot: "bg-emerald-400", ring: "ring-emerald-500/30" },
    comp_access:         { dot: "bg-sky-400",     ring: "ring-sky-500/30" },
    suspended:           { dot: "bg-amber-400",   ring: "ring-amber-500/30" },
    none:                { dot: "bg-zinc-600",    ring: "ring-zinc-700" },
};

export function CoverageDot({ coverage, athleteId, size = "sm" }) {
    if (!coverage) return null;
    const palette = SOURCE_COLORS[coverage.source] || SOURCE_COLORS.none;
    const titleSrc = {
        subscription: "Active subscription",
        family_subscription: "Family-wide subscription",
        session_pack: "Session pack",
        family_pack: "Family-wide pack",
        comp_access: "Comp access",
        suspended: "Access suspended (past due)",
        none: "No active coverage",
    }[coverage.source] || coverage.source;
    const title = `${titleSrc} — ${coverage.label}`;
    const dim = size === "xs" ? "w-1.5 h-1.5" : "w-2 h-2";
    return (
        <span
            data-testid={athleteId ? `coverage-dot-${athleteId}` : "coverage-dot"}
            data-covered={coverage.covered ? "true" : "false"}
            data-source={coverage.source}
            title={title}
            aria-label={title}
            className={`inline-block rounded-full ring-2 ${palette.dot} ${palette.ring} ${dim}`}
        />
    );
}

export default CoverageDot;
